<template>
  <section
    ref="target"
    v-editable="blok"
    :class="[pageIntroClass, themeClass, paddingClass]"
    class="block page-intro"
  >
    <div
      :class="{ 'with-filters': hasFilterSlot }"
      class="page-intro__inner flex align-center"
    >
      <MediaImage
        v-if="hasMedia && showMedia"
        :max-width="1440"
        :media="blok.image"
        :quality="75"
        fetch-priority="high"
        loading="eager"
      />
      <div class="container">
        <div class="page-intro__content">
          <NavigationBreadcrumbs v-if="breadcrumbs" />
          <h1 v-if="title">
            {{ title }}
          </h1>
          <h2 v-if="blok.introduction" class="h3 light whitespace-pre-line">
            {{ blok.introduction }}
          </h2>

          <slot name="meta" />
          <StoryblokComponent
            v-for="block in blok.blocks"
            :key="block._uid"
            :blok="block"
          />
          <BlockButton v-if="pageIntroCta" :blok="pageIntroCta" />
        </div>
        <slot name="filters" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PageIntroStoryblok } from '~/types/storyblok'
const target = ref()
const targetIsVisible = useElementVisibility(target, {
  threshold: 1,
})
watch(targetIsVisible, onElementVisible)

const props = withDefaults(
  defineProps<{
    blok: PageIntroStoryblok
    breadcrumbs?: boolean
    extraPadding?: boolean
  }>(),
  { breadcrumbs: true },
)
const { title } = useStoryContent({ blok: toRef(props, 'blok') })
const router = useRouter()
const route = useRoute()
const slots = useSlots()
const hasMedia = computed(() => !!props.blok?.image?.filename)
const showMedia = computed(() => props.blok?.type !== 'no-image')
const pageIntroClass = computed(() =>
  hasMedia.value ? 'page-intro--with-media' : 'page-intro--basic',
)
const pageIntroCta = computed(() => props.blok?.ctaButton?.[0])
const bgColor = computed(() => props.blok?.background || 'default')
const themeClass = computed(() => 'theme--' + bgColor.value)
const paddingClass = computed(() =>
  props.extraPadding ? 'page-intro--padding' : '',
)
const hasSlot = ref(false)
const hasFilterSlot = ref(false)

onMounted(() => {
  hasSlot.value = !!slots.default
  hasFilterSlot.value = !!slots.filters
})

function onElementVisible(isVisible: boolean) {
  if (isVisible) {
    router.push({
      hash: '',
      query: route.query,
    })
  }
}
</script>

<style lang="less" scoped>
section.page-intro {
  .relative();

  z-index: 10;

  .page-intro__inner {
    .relative();

    background: @color-fill-text;
    overflow: hidden;

    .page-intro__content {
      .relative();

      z-index: 10;
      padding: @gutter-lg 0;
      max-width: 75%;
      text-shadow: @3px @9px @12px fade(@color-fill-dark, 7.5%);

      @media @tablet {
        max-width: @640px;
      }

      @media @desktop {
        padding: @gutter-xxl 0;
      }
    }
  }

  &.page-intro--with-media {
    .page-intro__inner {
      .height-aspect-header();

      @media @tablet {
        .height-aspect-header-tablet();
      }

      @media @desktop {
        .height-aspect-header-desktop();
      }
    }
  }

  &.page-intro--basic {
    .page-intro__inner {
      padding: @72px 0;
      height: auto;

      &.with-filters {
        padding-bottom: @gutter;
        overflow: visible;
      }
    }
    .page-intro__content {
      padding: 0;
    }
  }

  &.theme--default {
    .page-intro__inner {
      background: @color-fill-text;

      .page-intro__content {
        .text-reverse();
      }
    }
  }

  &.theme--white {
    .page-intro__inner {
      background: @color-fill-normal;
    }
  }

  &.theme--grey {
    .page-intro__inner {
      background: @color-fill-alternate;
    }
  }

  &.page-intro--padding {
    .page-intro__inner {
      @media @desktop {
        padding-bottom: @120px;
      }
    }
  }
}

@media print {
  section.page-intro {
    margin-top: @gutter-xs;
    padding: @gutter-lg @gutter-sm;
    border: solid @2px @color-border;

    .page-intro__inner {
      padding: 0 !important;
    }
  }
}
</style>

<style lang="less">
section.page-intro {
  .page-intro__inner {
    .media {
      .absolute-fill();
      .media__container {
        height: 100%;
        padding: 0;

        img {
          width: 100%;
          height: 100%;
        }
      }
    }
  }

  &.theme--grey {
    .intro__search {
      .formkit-outer[data-family='text'] .formkit-input-element--search {
        input {
          background-color: @color-fill-normal;
        }
      }
    }
  }
}

section.page-intro.page-intro--basic {
  .page-intro--search & {
    h1 {
      .h3.light();
      .font-xl();
      .font-bold();
    }
  }
}
</style>
